import { makeStyles, Grid } from '@material-ui/core'
import ContentWrapper from 'components/ContentWrapper/ContentWrapper'
import { GridGrow } from 'common/Grid/GridGrow'
import { SimpleReport } from 'common/Report/SimpleReport'
import { CreatedQueryBarChartReport } from './CreatedQueryBarChartReport'
import { QueryPriorityPieChartReport } from './QueryPriorityPieChartReport'
import { QueryTypePieChartReport } from './QueryTypePieChartReport'
import * as Icons from 'common/Icons/SvgIcons'
import { useLisaAuth } from 'hooks/useLisaAuth'
import { useGetTenantReportQuery } from 'services/api/tenantApi'
import { Report } from 'common/Report/Report'
import { WithReportLoader } from 'common/WithLoader/WithLoader'
import { Pie } from 'react-chartjs-2'
import { ChartData, ChartOptions } from 'chart.js'
import { ChartReportItem } from 'types'

const useStyles = makeStyles(() => ({
  reportIcon: {
    fill: '#ffffff',
    fontSize: '20px',
    marginLeft: '20%',
    marginTop: '5%'
  },
  transitionNameTextLg: {
    fontSize: 50,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  gaugeWrapper: {
    marginTop: -12
  },
  fleetSizeBackground: {
    borderRadius: 10,
    backgroundColor: '#80cffa'
  },
  aircraftTypesBackground: {
    borderRadius: 10,
    backgroundColor: '#be73cc'
  },
  openWpsBackground: {
    borderRadius: 10,
    backgroundColor: '#80e6a3'
  },
  openWosBackground: {
    borderRadius: 10,
    backgroundColor: '#b0a9e9'
  },
  openItemsBackground: {
    borderRadius: 10,
    backgroundColor: '#ff6961'
  },
  missingDfpsBackground: {
    borderRadius: 10,
    backgroundColor: '#ffbe5a'
  }
}))

const ReportsPage = () => {
  const classes = useStyles()
  const { currentTenant } = useLisaAuth()
  const { data: tenantReport } = useGetTenantReportQuery(currentTenant)
  const wpOpenCount = tenantReport?.wpOpenCount ?? 0
  const wpClosedCount = tenantReport?.wpClosedCount ?? 0
  const woOpenCount = tenantReport?.woOpenCount ?? 0
  const woClosedCount = tenantReport?.woClosedCount ?? 0
  const itemOpenCount = tenantReport?.itemOpenCount ?? 0
  const itemClosedCount = tenantReport?.itemClosedCount ?? 0
  const inOperationCount = tenantReport?.inOperationCount ?? 0
  const inTransitionCount = tenantReport?.inTransitionCount ?? 0

  const getConfig = (): ChartOptions => ({
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom'
      }
    }
  })

  const initialChartData = (categoryPercentage?: number): ChartData => ({
    labels: [],
    datasets: [
      {
        backgroundColor: [
          '#ff6961',
          '#f1aba7'
        ],
        maxBarThickness: 100,
        categoryPercentage,
        data: []
      }
    ]
  })

  const chartData = (data: ChartReportItem[]): ChartData => {
    return data.reduce<ChartData>((cd, { unit, value }) => {
      cd.labels?.push(`${unit}`)
      cd.datasets[0].data.push(value)
      return cd
    }, initialChartData(0.2))
  }

  const wpInitialChartData = (categoryPercentage?: number): ChartData => ({
    labels: [],
    datasets: [
      {
        backgroundColor: [
          '#6cc48a',
          '#b1e3c2'
        ],
        maxBarThickness: 100,
        categoryPercentage,
        data: []
      }
    ]
  })

  const wpChartData = (data: ChartReportItem[]): ChartData => {
    return data.reduce<ChartData>((cd, { unit, value }) => {
      cd.labels?.push(`${unit}`)
      cd.datasets[0].data.push(value)
      return cd
    }, wpInitialChartData(0.2))
  }

  const woInitialChartData = (categoryPercentage?: number): ChartData => ({
    labels: [],
    datasets: [
      {
        backgroundColor: [
          '#b0a9e9',
          '#d1cee8'
        ],
        maxBarThickness: 100,
        categoryPercentage,
        data: []
      }
    ]
  })

  const woChartData = (data: ChartReportItem[]): ChartData => {
    return data.reduce<ChartData>((cd, { unit, value }) => {
      cd.labels?.push(`${unit}`)
      cd.datasets[0].data.push(value)
      return cd
    }, woInitialChartData(0.2))
  }

  const assetInitialChartData = (categoryPercentage?: number): ChartData => ({
    labels: [],
    datasets: [
      {
        backgroundColor: [
          '#69abcf',
          '#80cffa'
        ],
        maxBarThickness: 100,
        categoryPercentage,
        data: []
      }
    ]
  })

  const assetChartData = (data: ChartReportItem[]): ChartData => {
    return data.reduce<ChartData>((cd, { unit, value }) => {
      cd.labels?.push(`${unit}`)
      cd.datasets[0].data.push(value)
      return cd
    }, assetInitialChartData(0.2))
  }

  function GetPercentage (mainNumberOfItems: number, restOfTheItems: number) {
    const allItems = mainNumberOfItems + restOfTheItems
    if (allItems === 0) {
      return 0
    }

    return Math.round(mainNumberOfItems * 10000 / allItems) / 100
  }

  return (
    <ContentWrapper newLayout>
      <GridGrow container spacing={2} direction="column">
        <Grid item container spacing={2} direction="row">
          <Grid item sm={2} md={2}>
            <SimpleReport
              title={'FLEET SIZE'}
              reportIcon={<div className={classes.fleetSizeBackground}>
                <Icons.FleetSize
                  className={classes.reportIcon}
                />
              </div>}
              reportValue={tenantReport?.fleetSize}
            >
            </SimpleReport>
          </Grid>
          <Grid item sm={2} md={2}>
            <SimpleReport
              title={'AIRCRAFT TYPES'}
              reportIcon={<div className={classes.aircraftTypesBackground}>
                <Icons.PlaneIco
                  className={classes.reportIcon}
                />
              </div>}
              reportValue={tenantReport?.aircraftTypeCount}
            >
            </SimpleReport>
          </Grid>
          <Grid item sm={2} md={2}>
            <SimpleReport
              title={'OPEN WPs'}
              reportIcon={<div className={classes.openWpsBackground}>
                <Icons.OpenWorkPackages
                  className={classes.reportIcon}
                />
              </div>}
              reportValue={tenantReport?.wpOpenCount}
            >
            </SimpleReport>
          </Grid>
          <Grid item sm={2} md={2}>
            <SimpleReport
              title={'OPEN WOs'}
              reportIcon={<div className={classes.openWosBackground}>
                <Icons.OpenWorkOrders
                  className={classes.reportIcon}
                />
              </div>}
              reportValue={tenantReport?.woOpenCount}
            >
            </SimpleReport>
          </Grid>
          <Grid item sm={2} md={2}>
            <SimpleReport
              title={'OPEN ITEMS'}
              reportIcon={<div className={classes.openItemsBackground}>
                <Icons.DocQuery
                  className={classes.reportIcon}
                />
              </div>}
              reportValue={tenantReport?.itemOpenCount}
            >
            </SimpleReport>
          </Grid>
          <Grid item sm={2} md={2}>
            <SimpleReport
              title={'MISSING DFPs'}
              reportIcon={<div className={classes.missingDfpsBackground}>
                <Icons.MissingDfps
                  className={classes.reportIcon}
                />
              </div>}
              reportValue={tenantReport?.missingDocumentCount}
            >
            </SimpleReport>
          </Grid>
        </Grid>
        <Grid item container spacing={2} direction="row">
          <Grid item sm={12} md={6}>
            <CreatedQueryBarChartReport
              tenantId={currentTenant}>
            </CreatedQueryBarChartReport>
          </Grid>
          <Grid item sm={12} md={6}>
            <Report
              title={'Open items overview'}
              info={'Open items versus closed items.'}>
              <WithReportLoader
                noWrapper
                loading={false}
                hasResults={true}>
                <Pie type={'pie'} data={chartData([{ unit: `open - ${GetPercentage(itemOpenCount, itemClosedCount)}%`, value: itemOpenCount }, { unit: `closed - ${GetPercentage(itemClosedCount, itemOpenCount)}%`, value: itemClosedCount }])} options={getConfig() } />
              </WithReportLoader>
            </Report>
          </Grid>
        </Grid>
        <Grid item container spacing={2} direction="row">
          <Grid item sm={12} md={4}>
            <QueryPriorityPieChartReport
              tenantId={currentTenant}>
            </QueryPriorityPieChartReport>
          </Grid>
          <Grid item sm={12} md={8}>
            <QueryTypePieChartReport
              tenantId={currentTenant}>
            </QueryTypePieChartReport>
          </Grid>
        </Grid>
        <Grid item container spacing={2} direction="row">
          <Grid item sm={12} md={4}>
            <Report
              title={'WP Status'}
              info={'Work packages status.'}>
              <WithReportLoader
                noWrapper
                loading={false}
                hasResults={true}>
                <Pie type={'pie'} data={wpChartData([{ unit: `open - ${GetPercentage(wpOpenCount, wpClosedCount)}%`, value: wpOpenCount }, { unit: `closed - ${GetPercentage(wpClosedCount, wpOpenCount)}%`, value: wpClosedCount }])} options={getConfig() } />
              </WithReportLoader>
            </Report>
          </Grid>
          <Grid item sm={12} md={4}>
            <Report
              title={'WO Status'}
              info={'Work orders status.'}>
              <WithReportLoader
                noWrapper
                loading={false}
                hasResults={true}>
                <Pie type={'pie'} data={woChartData([{ unit: `open - ${GetPercentage(woOpenCount, woClosedCount)}%`, value: woOpenCount }, { unit: `closed - ${GetPercentage(woClosedCount, woOpenCount)}%`, value: woClosedCount }])} options={getConfig() } />
              </WithReportLoader>
            </Report>
          </Grid>
          <Grid item sm={12} md={4}>
            <Report
              title={'Asset status'}
              info={'In operation vs in transition.'}>
              <WithReportLoader
                noWrapper
                loading={false}
                hasResults={true}>
                <Pie type={'pie'} data={assetChartData([{ unit: `in operation - ${GetPercentage(inOperationCount, inTransitionCount)}%`, value: inOperationCount }, { unit: `in transition - ${GetPercentage(inTransitionCount, inOperationCount)}%`, value: inTransitionCount }])} options={getConfig() } />
              </WithReportLoader>
            </Report>
          </Grid>
        </Grid>
      </GridGrow>
    </ContentWrapper>
  )
}

ReportsPage.propTypes = {}

export default ReportsPage
